import { Alert, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import useDebounce from '../../../../hooks/useDebounce';
import { associateTestCasesBulk } from '../../../../modules/testcycles';
import { getTestSuiteRecommendation } from '../../../../modules/testsuites';
import { testSuiteHeader } from '../../../../static-data/users';
import Modal from '../../../elements/Modal';
import SearchBar from '../../../elements/SearchBar';
import TestCasesTable from '../../../elements/TableComponent';
import SelectTestCasesFromSuiteModal from './SelectTestCasesFromSuiteModal';
import CustomIconButton from '../../../elements/CustomIconButton';
import { Add, ViewList } from '@mui/icons-material';
import { getConfigurations } from '../../../../modules/config';
import { useInvalidateQuery } from '../../../../query/apiQueries';
import ConfirmationModal from '../../../elements/ConfirmationModal';

const AddExistingTestCaseModal = ({
  show,
  handleClose,
  fetchLinkedTestCases,
  entityId,
  fetchTestCycleData,
}) => {
  const [order, setOrder] = useState('desc');
  const [searchValue, setSearchValue] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [currentSuite, setCurrentSuite] = useState(null);
  const [mode, setMode] = useState('suiteList');
  const { addNotification } = useNotification();
  const { projectId } = useParams();
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [technologyLayerOptions, setTechnologyLayerOptions] = useState([]);
  const [labelsOptions, setLabelsOptions] = useState([]);
  const invalidateQueries = useInvalidateQuery();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSuiteId, setPendingSuiteId] = useState(null);

  const fetchTestCases = async (searchValue) => {
    setLoading(true);
    try {
      const response = await getTestSuiteRecommendation(
        projectId,
        entityId,
        searchValue
      );
      setData(response.data);
      setError(null);
    } catch (error) {
      setError(error.message || 'Unable to fetch test cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const [moduleResponse, technologyLayerResponse, labelsResponse] = await Promise.all([
        getConfigurations('modules', projectId),
        getConfigurations('technologylayer', projectId),
        getConfigurations('labels', projectId),
      ]);
      if (moduleResponse) {
        setModuleOptions(moduleResponse.data || []);
      }
      if (technologyLayerResponse) {
        setTechnologyLayerOptions(technologyLayerResponse.data || []);
      }
      if (labelsResponse) {
        setLabelsOptions(labelsResponse.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (show) {
      fetchTestCases(searchValue);
      fetchConfig();
    }
  }, [show, debouncedSearchQuery]);

  const handleAddTestCases = async () => {
    setLoading(true);
    try {
      const uniqueTestCaseIds = Array.from(new Set(selectedTestCases));
      await associateTestCasesBulk(entityId, {
        testcases: uniqueTestCaseIds,
      });

      addNotification('success', 'Test cases linked successfully!');
      fetchLinkedTestCases();
      invalidateQueries([
        { key: 'testcycles', type: 'listWithParams' },
      ]);
      fetchTestCycleData();
      handleCloseButton();
    } catch (error) {
      const msg = error?.message || 'Failed to link test cases.';
      addNotification('danger', msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseButton = () => {
    setSearchValue('');
    setSelectedTestCases([]);
    setMode('suiteList');
    handleClose();
    setData([]);
  };

  const handleAddAllFromSuite = async (suiteId) => {
    setLoading(true);
    try {
      await associateTestCasesBulk(entityId, {
        testsuites: [suiteId],
      });

      addNotification('success', 'All approved test cases from suite added.');
      fetchLinkedTestCases();
      invalidateQueries([
        { key: 'testcycles', type: 'listWithParams' },
      ]);
      fetchTestCycleData();
    } catch (error) {
      const msg = error?.message || 'Failed to add test cases from suite.';
      addNotification('danger', msg);
    } finally {
      setLoading(false);
    }
  };
  const handleAddIconClick = (suiteId) => {
    setPendingSuiteId(suiteId);
    setShowConfirmation(true);
  };
   const handleConfirmAddAll = async () => {
    setShowConfirmation(false);
    await handleAddAllFromSuite(pendingSuiteId);
    setPendingSuiteId(null);
  };
  return (
    <>
    <Modal
      modalTitle="Select Test Suite(s)"
      open={show}
      handleClose={handleCloseButton}
      maxWidth="lg"
      action={() => (
        <>
          <Button
            title="Close"
            variant="outlined"
            color="secondary"
            onClick={handleCloseButton}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleAddTestCases}
            title="Add selected test cases"
            disabled={Object.values(selectedTestCases).flat().length === 0}>
            Add
          </Button>
        </>
      )}>
      <Container fluid className="mt-2">
        {Array.from(new Set(selectedTestCases)).length > 0 && (
          <Row className="mb-3">
            <Col>
              <Alert severity="info">
                {Array.from(new Set(selectedTestCases)).length} unique test
                case(s) selected.
              </Alert>
            </Col>
          </Row>
        )}
        {mode === 'suiteList' && (
          <>
            <Row className="mb-2">
              <Col>
                <Alert severity="success">
                  Test suites that have already been added can be re-added if
                  new test cases are available. Only approved test cases will be
                  included.
                </Alert>

                <Alert severity="info">
                  Previously added test suites appear in bold. The check icon
                  will be highlighted when new test cases have been added to
                  these suites.
                </Alert>
              </Col>
            </Row>
            <Row>
              <Col>
                <SearchBar
                  placeholder="Search test suite(s) to add"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  clearSearch={() => setSearchValue('')}
                />
              </Col>
            </Row>
            {error && <p className="text-danger mt-2 text-center">{error}</p>}
            <TestCasesTable
              data={data}
              headerTabs={testSuiteHeader}
              order={order}
              setOrder={setOrder}
              defaultSort="created_at"
              isLoading={loading}
              striped
              action={(row) => (
                <>
                  <CustomIconButton
                    title={row?.test_case_count === 0 ? "No approved test cases to add" : "Add all approved test cases from this suite"}
                    disabled={loading || row?.test_case_count === 0}
                    onClick={() => handleAddIconClick(row.id)}>
                    <Add sx={{ color: 'var(--success-color)' }} />
                  </CustomIconButton>

                  <CustomIconButton
                    title="View & Select Specific Test Cases"
                    onClick={() => {
                      setCurrentSuite(row);
                      setMode('testCases');
                    }}>
                    <ViewList sx={{ color: 'var(--primary-color)' }} />
                  </CustomIconButton>
                </>
              )}
            />
          </>
        )}

        {mode === 'testCases' && currentSuite && (
          <SelectTestCasesFromSuiteModal
            suite={currentSuite}
            selectedTestCases={selectedTestCases}
            setSelectedTestCases={setSelectedTestCases}
            handleBack={() => setMode('suiteList')}
            testCycleId={entityId}
            moduleOptions={moduleOptions}
            technologyLayerOptions={technologyLayerOptions}
            labelsOptions={labelsOptions}
          />
        )}
      </Container>
    </Modal>
      <ConfirmationModal
        show={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        handleConfirm={handleConfirmAddAll}
        title="Confirm Add Test Cases"
        body="Are you sure you want to add all the approved test cases from this test suite?"
        buttonLabel="Confirm"
        buttonLabelSecondary="Cancel"
        buttonStyle="primary"
        loading={loading}
      />
    </>  
  );
};

export default AddExistingTestCaseModal;
